class SlumberHotspotBanner extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.activePopupState = null;
    this.suppressOutsideCloseUntil = 0;
  }

  connectedCallback() {
    this.banner = this.querySelector('.slumber-hotspot-banner');
    this.media = this.querySelector('.slumber-hotspot-banner__media');
    this.hotspots = Array.from(this.querySelectorAll('.slumber-hotspot'));
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    this.hotspots.forEach((hotspot) => {
      const trigger = hotspot.querySelector('.slumber-hotspot__trigger');
      if (!trigger) return;

      trigger.addEventListener('click', (event) => {
        if (!this.mobileQuery.matches) return;

        event.preventDefault();
        event.stopPropagation();
        this.toggleHotspot(hotspot);
      });
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    this.restorePopupDom();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
  }

  isMobile() {
    return this.mobileQuery.matches;
  }

  toggleHotspot(hotspot) {
    const isActive = hotspot.classList.contains('is-active');

    this.closeAll();

    if (!isActive) {
      this.suppressOutsideCloseUntil = Date.now() + 350;
      hotspot.classList.add('is-active');
      hotspot.querySelector('.slumber-hotspot__trigger')?.setAttribute('aria-expanded', 'true');
      this.banner?.classList.add('is-popup-open');
      window.requestAnimationFrame(() => this.openMobilePopup(hotspot));
    }
  }

  closeAll() {
    this.hotspots.forEach((hotspot) => {
      hotspot.classList.remove('is-active');
      hotspot.querySelector('.slumber-hotspot__trigger')?.setAttribute('aria-expanded', 'false');
    });

    this.restorePopupDom();
    this.banner?.classList.remove('is-popup-open');
  }

  openMobilePopup(hotspot) {
    if (!this.isMobile() || !this.media) return;

    const popup = hotspot.querySelector('.slumber-hotspot__popup');
    if (!popup) return;

    const placeholder = document.createComment('hotspot-popup-anchor');
    popup.before(placeholder);

    this.activePopupState = { hotspot, popup, placeholder };
    this.media.appendChild(popup);
    popup.classList.add('slumber-hotspot__popup--mobile-portal');

    this.positionMobilePopup();
  }

  restorePopupDom() {
    if (!this.activePopupState) return;

    const { popup, placeholder } = this.activePopupState;

    popup.classList.remove('slumber-hotspot__popup--mobile-portal');
    this.resetPopupStyles(popup);

    if (placeholder.isConnected) {
      placeholder.replaceWith(popup);
    }

    this.activePopupState = null;
  }

  resetPopupStyles(popup) {
    if (!popup) return;

    popup.style.removeProperty('position');
    popup.style.removeProperty('left');
    popup.style.removeProperty('top');
    popup.style.removeProperty('bottom');
    popup.style.removeProperty('width');
    popup.style.removeProperty('max-width');
    popup.style.removeProperty('transform');
    popup.style.removeProperty('z-index');
    popup.style.removeProperty('box-sizing');
  }

  positionMobilePopup() {
    const state = this.activePopupState;
    if (!state || !this.isMobile() || !this.media) return;

    const { hotspot, popup } = state;
    const trigger = hotspot.querySelector('.slumber-hotspot__trigger');
    if (!trigger) return;

    this.resetPopupStyles(popup);

    const padding = 12;
    const gap = 10;
    const containerRect = this.media.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    popup.style.position = 'absolute';
    popup.style.boxSizing = 'border-box';
    popup.style.width = `${Math.max(0, containerRect.width - padding * 2)}px`;
    popup.style.transform = 'none';
    popup.style.bottom = 'auto';
    popup.style.zIndex = '20';

    const popupHeight = popup.offsetHeight;
    const popupWidth = popup.offsetWidth;

    let left = triggerRect.left - containerRect.left + triggerRect.width / 2 - popupWidth / 2;
    left = Math.max(padding, Math.min(left, containerRect.width - padding - popupWidth));

    const preferTop = hotspot.classList.contains('slumber-hotspot--popup-top');
    const triggerTop = triggerRect.top - containerRect.top;
    const triggerBottom = triggerRect.bottom - containerRect.top;
    let top;

    if (preferTop && triggerTop >= popupHeight + gap + padding) {
      top = triggerTop - gap - popupHeight;
    } else if (containerRect.height - triggerBottom >= popupHeight + gap + padding) {
      top = triggerBottom + gap;
    } else if (triggerTop >= containerRect.height - triggerBottom) {
      top = Math.max(padding, triggerTop - gap - popupHeight);
    } else {
      top = Math.min(containerRect.height - padding - popupHeight, triggerBottom + gap);
    }

    top = Math.max(padding, Math.min(top, containerRect.height - padding - popupHeight));

    popup.style.left = `${left}px`;
    popup.style.top = `${top}px`;
  }

  onDocumentClick(event) {
    if (!this.isMobile()) return;
    if (Date.now() < this.suppressOutsideCloseUntil) return;
    if (event.target.closest('.slumber-hotspot__trigger, .slumber-hotspot__popup')) return;
    if (!this.querySelector('.slumber-hotspot.is-active')) return;

    this.closeAll();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.closeAll();
  }

  onResize() {
    if (!this.isMobile()) return;
    if (this.activePopupState) this.positionMobilePopup();
  }
}

customElements.define('slumber-hotspot-banner', SlumberHotspotBanner);
